import { TProduct as BaseProduct } from 'types/models/product';
import { debounce } from 'widgets/toolbox/debounce';
import { pageProperties, report, getImpressionCategory } from '../utils/base';

type TProduct = BaseProduct & { index: number; item_list_id: string };

export type TImpression = {
    item_id: string;
    quantity: number;
    index?: number;
    item_name: string;
    item_list_id: string;
    price?: string;
    item_category?: string;
    item_brand?: string;
    coupon?: Array<string>;
    item_variant?: string;
};

declare global {
    interface Window {
        gtmImpressions?: {
            event: string;
            ecommerce?: {
                currencyCode: string;
                impressions?: Array<TImpression>;
            };
        };
        productPositions?: {
            [key: string]: number;
        };
    }
}

/**
 * @description GTM impressions
 * @module impressions
 * @category widgets
 * @subcategory int_gtm
 */

export let impressionsDebounce: Array<TImpression> = [];

export const impressedProducts: Array<string> = [];

export function getBatchedImpressions(items: Array<TImpression>) {
    const BATCH_SIZE = 24;
    const batches: Array<Array<TImpression>> = [];

    for (let i = 0; i < items.length; i += BATCH_SIZE) {
        batches.push(items.slice(i, i + BATCH_SIZE));
    }

    return batches;
}

/**
 * @description Added product to impressed product list
 * @param product - product with id, position and variant
 */
export function addToImpressedProducts(product: TImpression) {
    const impressionUniqueId = `${product.item_id}|${product.index || ''}|${product.item_variant || ''}`;

    impressedProducts.push(impressionUniqueId);
}

/**
 * @description Checks if product is already impressed
 * @param id -product ID
 * @param position  - product position
 * @param variant - product variant
 * @returns result
 */
export function hasBeenImpressed(id: string, position?: string | number, variant?: string): boolean {
    const impressionUniqueId = `${id}|${position || ''}|${variant || ''}`;

    return impressedProducts.includes(impressionUniqueId);
}

/**
 * @description Returns wishlist products impression data
 * @param products - products data
 * @returns result
 */
export function getWishlistImpressions(products: Array<TProduct>) {
    const impressions: Array<TImpression> = [];

    if (!products?.length) {
        return impressions;
    }

    for (let index = 0; index < products.length; index++) {
        const product = products[index];

        if (product && !hasBeenImpressed(product.id, String(product.index), product.gtmInfo?.item_variant) && product.gtmInfo) {
            impressions.push({
                item_list_id: 'Wishlist',
                index: product.index,
                ...product.gtmInfo
            });
        }
    }

    if (!impressions.length) {
        return impressions;
    }

    return impressions;
}

/**
 * @description Reports GTM promo impressions on page load
 */
export function reportPromoImpressions(): void {
    let promoImpressions: Array<HTMLElement> = Array.from(document.querySelectorAll('.js-promo-impression'));

    promoImpressions = promoImpressions.map((impressionEl) => {
        try {
            const promoDetails = JSON.parse(<string>impressionEl.dataset.promoDetails);

            /**
             * @description Tracks to dataLayer an information about click on promotion details
             * @listens dom:click
             */
            impressionEl.addEventListener('click', () => {
                report('view_promotion', {
                    promotions: [promoDetails]
                });
            }, { passive: true, once: true });

            return promoDetails;
        } catch (e) {
            setTimeout(() => {
                throw e;
            }, 0);

            return false;
        }
    }).filter(Boolean);

    if (promoImpressions.length) {
        report('pageLoad', {
            promotions: promoImpressions
        });
    }
}

/**
 * @description Reports GTM product impressions on page load
 */
export function reportProductImpressions(): void {
    if (!window.gtmImpressions?.ecommerce?.impressions?.length) {
        return;
    }

    const impressedProductsList = window.gtmImpressions.ecommerce.impressions.filter(impression => {
        // view item list from any carousel will not be triggered
        return impression.item_list_id !== 'Products Carousel';
    });

    impressedProductsList.forEach(impression => {
        const position = <number> impression.index || window.productPositions?.[impression.item_id];

        if (hasBeenImpressed(impression.item_id, position, impression.item_variant)) {
            return;
        }

        if (position) {
            impression.index = position;


            if (window.location.search.indexOf('q=') > -1) {
                impression.item_list_id = pageProperties.searchList;
            } else {
                const category = getImpressionCategory();

                impression.item_list_id = category || impression.item_list_id;
            }
        }

        addToImpressedProducts(impression);
    });

    const batchedImpressions = getBatchedImpressions(impressedProductsList);

    batchedImpressions.forEach(batch => {
        report('view_item_list', {
            ecommerce: {
                item_list_id: batch[0].item_list_id || getImpressionCategory() || '',
                items: batch
            }
        });
    });
}

/**
 * @description Sends products impressions to dataLayer
 */
export const reportImpression = debounce(() => {
    const batches = getBatchedImpressions(impressionsDebounce);

    batches.forEach(batch => {
        report('view_item_list', {
            ecommerce: {
                item_list_id: batch[0].item_list_id || getImpressionCategory() || '',
                items: batch
            }
        });
    });

    impressionsDebounce = [];
}, 50);
